import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount} from '@philips-internal/oswald-ui-core/lib/client';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FragmentNames, GastherapyReverseOrInterruptAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import MedicationService from '../../../services/medication-service';
import { Inject } from '@odinjs/odin';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AdminModalsService } from '../../../services/admin-modals-service';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import GastherapyService, { CurrentRecordRevert, GastherapyItemStatus, GastherapyProps } from '../gastherapy-service';
import { Value } from '@philips-internal/oswald-ui-core';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { ReservedProduction } from '../../blood-management/blood-management-types';
import { YesNoType } from '../../../../../utils/enum-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../../utils/parameters';
import { FoundationUtils } from '../../../../../utils/foundation-utils';

@Controller({ component: GastherapyReverseOrInterruptAliases.GASTHERAPY_REVERSE_OR_INTERRUPT_LAYOUT, fragment: FragmentNames.GASTHERAPY_REVERSE_OR_INTERRUPT })
export default class GastherapyreverseController extends BaseController {

  @Inject(GastherapyReverseOrInterruptAliases.REVERSE_OR_INTERRUPT_FORM)
  private formApi!: FormAPI;

  @Inject(GastherapyReverseOrInterruptAliases.REVERSE_REASON_FIELD)
  private reverseReasonField!: FieldAPI;

  @Inject(GastherapyReverseOrInterruptAliases.INTERRUPT_REASON_FIELD)
  private interruptReasonField!: FieldAPI;

  @Inject(GastherapyReverseOrInterruptAliases.DATE_FIELD)
  private dateField!: FieldAPI;

  @OnMount(GastherapyReverseOrInterruptAliases.GASTHERAPY_REVERSE_OR_INTERRUPT_LAYOUT)
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const gastherapyReverse = this.context.getBulkData() as unknown as ReservedProduction;
    this.context.set({gastherapyReverse: { ...gastherapyReverse, encounterId, patientId }})
    InformationBarUtils.collapsedBar = true;
  }

  @OnMount([GastherapyReverseOrInterruptAliases.TEXT_GASTHERAPY_DATE, GastherapyReverseOrInterruptAliases.TEXT_GASTHERAPY_INFORMATION])
  handleMountTexts({ target }: EventData<TextAPI>): void {
    const data = TimelineContext.get() as TimeLineEventMedication;
    const value = target.getValue()
      .replace(':gastherapy-information', data.description as string)
      .replace(':gastherapy-date', DateFormatterUtil.timeStamp(String(data.start)));

    target.setValue(value);
  }

  private setDateVisible(): void {
    const specifyTheEventDate = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.SPECIFY_THE_DATE_OF_THE_EVENT_WHEN_RESTARTING_INTERRUPTING_OR_SUSPENDING_THE_GASTHERAPY) === YesNoType.YES;
    const actionTypeItem = this.context.getBulkData().contextItem as GastherapyProps;
    const dateVisibleByCheckingParameter222 = specifyTheEventDate && actionTypeItem.actionType === GastherapyItemStatus.INTERRUPTED;
    const dateVisibleReverse = actionTypeItem.actionType === GastherapyItemStatus.REVERSE;
    this.dateField.setVisible(dateVisibleByCheckingParameter222 || dateVisibleReverse);
  }

  @OnMount(GastherapyReverseOrInterruptAliases.DATE_INPUT)
  handleDateFieldMount(event: MountEvent): void {
    this.setDateVisible();
    if(this.dateField.isVisible()){
      const api = event.target as InputAPI;
      const currentTimeMoment = moment();
      this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
      api.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
    }
  }

  @OnBeforeCancel(GastherapyReverseOrInterruptAliases.REVERSE_OR_INTERRUPT_FORM)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnBeforeSubmit(GastherapyReverseOrInterruptAliases.REVERSE_OR_INTERRUPT_FORM)
  handleSubmitForm(): void {
    const actionTypeItem = this.context.getBulkData().contextItem as GastherapyProps;
    switch (actionTypeItem.actionType) {
      case String(GastherapyItemStatus.REVERSE):
        this.reverseGastherapy();
        break;
      case String(GastherapyItemStatus.INTERRUPTED):
        this.interruptGastherapy();
        break;
      default: undefined;
    }
  }

  private interruptGastherapy(): void {
    const body = this.getBody(GastherapyItemStatus.INTERRUPTED);
    this.reverseOrInterurptGastherapy(body);
  }

  private reverseGastherapy(): void {
    const body = this.getBody(GastherapyItemStatus.REVERSE);
    this.reverseOrInterurptGastherapy(body);
  }

  private getBody(changeType: string): Record<string, Value> {
    const currentRecord = this.formApi.getValue();
    const timelineGastherapy = TimelineContext.get() as TimeLineEventMedication;
    const interruptReason = currentRecord.reason as GastherapyProps;
    const reverseReason = currentRecord.reason as CurrentRecordRevert;
    const justification = currentRecord.observation ?? currentRecord.observation;

    return {
      dtChange: moment().utc().format(ISO_FULL_DATE_FORMAT),
      ieChange: changeType,
      justification,
      nrSeqReason: interruptReason.sequenceId || reverseReason.sequenceNumber,
      prescriptionNumber: timelineGastherapy.prescriptionNumber,
      scheduleSeq: timelineGastherapy.id,
      seqGastherapy: timelineGastherapy.itemSequence,
      seqGastherapyCpoe: timelineGastherapy.cpoeSequence,
    } as Record<string, Value>;
  }

  @OnMount(GastherapyReverseOrInterruptAliases.REVERSE_REASON_FETCH)
  handleReverseFetchReasonMount(event: MountEvent): void {
    const fetchReverseReason = event.target as FetchAPI;
    fetchReverseReason.setParameters({ ieTypeEvent: this.reasonFetch() }, { skip: true });
  }

  @OnMount(GastherapyReverseOrInterruptAliases.INTERRUPT_REASON_FETCH)
  handleInterruptFetchReasonMount(event: MountEvent): void {
    const fetchInterruptReason = event.target as FetchAPI;
    fetchInterruptReason.setParameters({ ieTypeEvent: this.reasonFetch() }, { skip: true });
  }

  reasonFetch(): string {
    const actionTypeItem = this.context.getBulkData().contextItem as GastherapyProps;
    switch(actionTypeItem.actionType) {
      case String(GastherapyItemStatus.REVERSE):
        this.interruptReasonField.setVisible(false);
        return 'R';
      case String(GastherapyItemStatus.INTERRUPTED):
        this.reverseReasonField.setVisible(false);
        return 'I';
      default: return '';
    }
  }

  private reverseOrInterurptGastherapy(body: Record<string, Value>): void {
    const actionTypeItem = this.context.getBulkData().contextItem as GastherapyProps;

    GastherapyService.generateGasTherapyChange(body)
      .then(() => {
        if(actionTypeItem.actionType === GastherapyItemStatus.INTERRUPTED){
          MedicationService.showToast(MessageService.translate('text.interrupt-gastherapy-event'), ToastLevel.SUCCESS);
        } else {
          MedicationService.showToast(MessageService.translate('text.revert-event'), ToastLevel.SUCCESS);
        }
        this.redirectToTimeline();
      })
      .catch((error) => {
        const message = error.body.messageDescription;
        const errorMessage = error.body.errors.join('; ');
        MedicationService.showToast(message ?? errorMessage, ToastLevel.ERROR);
      });
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.GASTHERAPY_REVERSE_OR_INTERRUPT);
    AdminModalsService.redirectToTimeline(FragmentNames.GASTHERAPY_VIEWER);
  }
}
